import { useState } from 'react';
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Link,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import FormBase from '@/components/forms/new/FormBase';
import { Button } from '@/components/v4';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { useQuery } from 'hooks/useCustomQuery';
import { validationSchema } from './consts';
import walkthroughIds from './walkthroughIds';
import { updateCatalogItem, deleteCatalogItem, getCatalogItems } from './api';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import NameField from './formFields/NameField';
import ParentField from './formFields/ParentField';
import DescriptionField from './formFields/DescriptionField';
import SkuField from './formFields/SkuField';
import OfferingIdField from './formFields/OfferingIdField';
import VisibilityField from './formFields/VisibilityField';
import RuleField from './formFields/RuleField';

const breadCrumbTrail = [
    { label: '8×8 Catalog', path: '/globaladmin/8x8-catalog' },
    { label: 'Edit Offering', path: '' },
];

export default function EightByEightCatalogEdit({
    rowData,
    setCatalogAction,
    setSuccessAction,
    platformId,
    requiredScope,
}) {
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const {
        isOpen: isDeleteModalOpen,
        onOpen: onDeleteModalOpen,
        onClose: onDeleteModalClose,
    } = useDisclosure();

    const { darkMode } = useSelector((state) => state.settings);
    const { createMode } = walkthroughIds;

    const { data: catalogItems = [], isLoading: isLoadingItems } = useQuery({
        queryKey: ['8x8CatalogItems', platformId],
        queryFn: () =>
            getCatalogItems(platformId, requiredScope).then((r) => r.data),
        staleTime: 0,
        cacheTime: 0,
        enabled: !!platformId,
        refetchOnWindowFocus: false,
    });

    if (isLoadingItems) return <LoadingIndicator />;

    const initialValues = {
        name: rowData?.name || '',
        parentId: rowData?.parentId || '',
        description: rowData?.description || '',
        sku: rowData?.sku || '',
        offeringId: rowData?.offeringId || '',
        visibility: rowData?.visibility || 'visible',
        rule:
            rowData?.rule === 'optional' || rowData?.rule === 'can'
                ? 'optional'
                : rowData?.rule === 'required' || rowData?.rule === 'must'
                  ? 'required'
                  : '',
    };

    const finalFormSubmit = async (fields, actions) => {
        setIsSubmitting(true);
        setError(null);
        setSuccessAction(null);

        updateCatalogItem(
            rowData.id,
            {
                ...fields,
                parentId: fields.parentId || null,
                rule: fields.rule || null,
            },
            requiredScope,
        )
            .then((response) => {
                if (response.status === 200) {
                    setSuccessAction('Edit');
                    setCatalogAction('Get');
                    setTimeout(() => setSuccessAction(null), 6000);
                } else {
                    setError(
                        response.response?.data?.message ||
                            'Failed to update catalog item.',
                    );
                }
            })
            .catch((err) => {
                setError(
                    err?.response?.data?.message ||
                        err?.data?.message ||
                        'Something went wrong when updating this catalog item.',
                );
            })
            .finally(() => {
                setIsSubmitting(false);
                actions.setSubmitting(false);
            });
    };

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        <BreadcrumbLink
                            as={Link}
                            to={crumb.path}
                            onClick={
                                index === 0
                                    ? () => setCatalogAction('Get')
                                    : undefined
                            }>
                            {crumb.label}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>
            <Text sx={{ fontSize: '20', fontWeight: '500' }}>
                Edit Offering
            </Text>
            <FormBase
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                validationSchema={validationSchema}
                ufBackBtn={createMode.ufBackBtn}
                ufSaveBtn={createMode.ufEditBtn}
                customClick={true}
                extraButton={
                    <Box w="100%" display="flex" justifyContent="flex-end">
                        <Button
                            w={142}
                            colorScheme="red"
                            onClick={onDeleteModalOpen}
                            isDisabled={isDeleting || isSubmitting}>
                            Delete
                        </Button>
                    </Box>
                }
                onBack={() => setCatalogAction('Get')}>
                {({ errors = {}, touched = {} }) =>
                    [
                        <NameField
                            key="name"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <ParentField
                            key="parentId"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                            catalogItems={catalogItems}
                            excludeId={rowData?.id}
                        />,
                        <DescriptionField
                            key="description"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <SkuField
                            key="sku"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <OfferingIdField
                            key="offeringId"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <VisibilityField
                            key="visibility"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <RuleField key="rule" darkMode={darkMode} />,
                        error && (
                            <Alert
                                key="error"
                                status="error"
                                sx={{ margin: '25px 0' }}>
                                <AlertIcon />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ),
                    ].filter(Boolean)
                }
            </FormBase>

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={onDeleteModalClose}
                catalogItemName={rowData?.name}
                onConfirm={async () => {
                    try {
                        setIsDeleting(true);
                        await deleteCatalogItem(rowData.id, requiredScope);
                        setSuccessAction('Delete');
                        setCatalogAction('Get');
                        setTimeout(() => setSuccessAction(null), 6000);
                    } catch (err) {
                        setError(
                            err?.response?.data?.message ||
                                err?.data?.message ||
                                'Failed to delete catalog item.',
                        );
                    } finally {
                        setIsDeleting(false);
                        onDeleteModalClose();
                    }
                }}
                isDeleting={isDeleting}
            />
        </>
    );
}
